import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { PaidOvertimeService } from "../../services/paidOvertimeService";
import logger from "../../core/utils/logger";

export const fetchPaidOvertimeByUser = createAsyncThunk(
    'paidOvertime/fetchByUser',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await PaidOvertimeService.getByUser(userId);
            logger.log("Paid overtime by user response:", JSON.stringify(response));
            return response;
        } catch (error) {
            logger.error('Error:', error);
            const errorMessage =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                'Error getting paid overtime';
            return rejectWithValue(errorMessage);
        }
    }
);

export const createPaidOvertime = createAsyncThunk(
    'paidOvertime/create',
    async (data, { rejectWithValue }) => {
        try {
            const response = await PaidOvertimeService.create(data);
            logger.log("Create paid overtime response:", JSON.stringify(response));
            return response;
        } catch (error) {
            logger.error('Error:', error);
            const errorMessage =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                'Error creating paid overtime';
            return rejectWithValue(errorMessage);
        }
    }
);

export const updatePaidOvertime = createAsyncThunk(
    'paidOvertime/update',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            await PaidOvertimeService.update(id, data);
            logger.log("Updated paid overtime:", id);
            return { id, data };
        } catch (error) {
            logger.error('Error:', error);
            const errorMessage =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                'Error updating paid overtime';
            return rejectWithValue(errorMessage);
        }
    }
);

export const fetchMyPaidOvertime = createAsyncThunk(
    'paidOvertime/fetchMy',
    async (_, { rejectWithValue }) => {
        try {
            const response = await PaidOvertimeService.getMyOvertime();
            logger.log("My paid overtime response:", JSON.stringify(response));
            return response;
        } catch (error) {
            logger.error('Error:', error);
            const errorMessage =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                'Error getting my paid overtime';
            return rejectWithValue(errorMessage);
        }
    }
);

export const deletePaidOvertime = createAsyncThunk(
    'paidOvertime/delete',
    async (id, { rejectWithValue }) => {
        try {
            await PaidOvertimeService.delete(id);
            logger.log("Deleted paid overtime:", id);
            return id;
        } catch (error) {
            logger.error('Error:', error);
            const errorMessage =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                'Error deleting paid overtime';
            return rejectWithValue(errorMessage);
        }
    }
);

const initialState = {
    paidOvertimeEntries: [],
    loading: false,
    error: null,
    myPaidOvertimeEntries: [],
    myLoading: false,
    myError: null
};

const paidOvertimeSlice = createSlice({
    name: 'paidOvertime',
    initialState,
    reducers: {
        clearPaidOvertimeState: (state) => {
            state.paidOvertimeEntries = [];
            state.loading = false;
            state.error = null;
        },
        clearMyPaidOvertimeState: (state) => {
            state.myPaidOvertimeEntries = [];
            state.myLoading = false;
            state.myError = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch by user
        builder.addCase(fetchPaidOvertimeByUser.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchPaidOvertimeByUser.fulfilled, (state, action) => {
            state.paidOvertimeEntries = action.payload;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(fetchPaidOvertimeByUser.rejected, (state, action) => {
            state.paidOvertimeEntries = [];
            state.loading = false;
            state.error = action.payload || action.error.message;
        });

        // Fetch my overtime
        builder.addCase(fetchMyPaidOvertime.pending, (state) => {
            state.myLoading = true;
            state.myError = null;
        });
        builder.addCase(fetchMyPaidOvertime.fulfilled, (state, action) => {
            state.myPaidOvertimeEntries = action.payload;
            state.myLoading = false;
            state.myError = null;
        });
        builder.addCase(fetchMyPaidOvertime.rejected, (state, action) => {
            state.myPaidOvertimeEntries = [];
            state.myLoading = false;
            state.myError = action.payload || action.error.message;
        });

        // Create
        builder.addCase(createPaidOvertime.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createPaidOvertime.fulfilled, (state, action) => {
            state.paidOvertimeEntries.unshift(action.payload);
            state.loading = false;
            state.error = null;
        });
        builder.addCase(createPaidOvertime.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error.message;
        });

        // Update
        builder.addCase(updatePaidOvertime.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updatePaidOvertime.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
        });
        builder.addCase(updatePaidOvertime.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error.message;
        });

        // Delete
        builder.addCase(deletePaidOvertime.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deletePaidOvertime.fulfilled, (state, action) => {
            state.paidOvertimeEntries = state.paidOvertimeEntries.filter(
                entry => entry.id !== action.payload
            );
            state.loading = false;
            state.error = null;
        });
        builder.addCase(deletePaidOvertime.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error.message;
        });
    }
});

export const { clearPaidOvertimeState, clearMyPaidOvertimeState } = paidOvertimeSlice.actions;
export default paidOvertimeSlice.reducer;
